<template>
  <div>
    <transition name="quick-replies-fade">
      <div
        v-if="open"
        class="quick-replies__backdrop"
        @click="$emit('close')"
      />
    </transition>
    <transition name="quick-replies-slide">
      <aside
        v-if="open"
        class="quick-replies"
        role="dialog"
        aria-modal="true"
        aria-label="Quick replies"
      >
        <header class="quick-replies__header">
          <div class="quick-replies__title-block">
            <span class="quick-replies__accent" aria-hidden="true"></span>
            <div>
              <h2 class="quick-replies__title">Quick Replies</h2>
              <p class="quick-replies__subtitle">Click any reply to copy it to your clipboard</p>
            </div>
          </div>
          <div class="quick-replies__header-actions">
            <button
              type="button"
              class="quick-replies__icon-btn"
              :class="{ 'is-loading': loading }"
              :disabled="loading"
              :title="loading ? 'Refreshing…' : 'Refresh'"
              aria-label="Refresh quick replies"
              @click="fetchReplies(true)"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
                <path d="M20.49 15A9 9 0 0 1 5.64 18.36L1 14"/>
              </svg>
            </button>
            <button
              type="button"
              class="quick-replies__icon-btn"
              title="Close"
              aria-label="Close quick replies"
              @click="$emit('close')"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </header>

        <div class="quick-replies__tabs" role="tablist">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            role="tab"
            class="quick-replies__tab"
            :class="{ 'quick-replies__tab--active': activeTab === tab.id }"
            :aria-selected="activeTab === tab.id"
            @click="activeTab = tab.id"
          >
            <span>{{ tab.label }}</span>
            <span class="quick-replies__tab-count">{{ countFor(tab.id) }}</span>
          </button>
        </div>

        <div class="quick-replies__search">
          <svg class="quick-replies__search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            placeholder="Search by title or text…"
            class="quick-replies__search-input"
            aria-label="Search quick replies"
          >
          <button
            v-if="search"
            type="button"
            class="quick-replies__search-clear"
            title="Clear"
            aria-label="Clear search"
            @click="search = ''"
          >
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="quick-replies__body">
          <div v-if="loading && !hasLoadedOnce" class="quick-replies__skeletons">
            <div v-for="n in 4" :key="n" class="quick-replies__skeleton"/>
          </div>

          <div v-else-if="error" class="quick-replies__state quick-replies__state--error">
            <p>Couldn't load replies.</p>
            <button type="button" class="quick-replies__retry" @click="fetchReplies(true)">Retry</button>
          </div>

          <ul v-else-if="filteredItems.length" class="quick-replies__list">
            <li
              v-for="item in filteredItems"
              :key="item.id"
              class="quick-replies__item"
              :class="{ 'quick-replies__item--copied': copiedId === item.id }"
              @click="copyItem(item)"
            >
              <div class="quick-replies__item-header">
                <span class="quick-replies__item-title">{{ item.title }}</span>
                <button
                  type="button"
                  class="quick-replies__copy-btn"
                  :title="copiedId === item.id ? 'Copied' : 'Copy text'"
                  :aria-label="copiedId === item.id ? 'Copied' : 'Copy text'"
                  @click.stop="copyItem(item)"
                >
                  <svg v-if="copiedId !== item.id" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </button>
              </div>
              <p
                class="quick-replies__item-text"
                :class="{ 'quick-replies__item-text--expanded': expandedId === item.id }"
              >{{ item.text }}</p>
              <div class="quick-replies__item-footer">
                <button
                  v-if="isLongText(item.text)"
                  type="button"
                  class="quick-replies__expand"
                  @click.stop="toggleExpand(item.id)"
                >
                  {{ expandedId === item.id ? 'Show less' : 'Show more' }}
                </button>
                <span v-else class="quick-replies__expand-spacer"/>
                <span v-if="copiedId === item.id" class="quick-replies__copied-flag">Copied</span>
              </div>
            </li>
          </ul>

          <div v-else class="quick-replies__state">
            <p v-if="search">No replies match "{{ search }}".</p>
            <p v-else>No {{ activeTabLabelLower }} yet.</p>
          </div>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'

const LONG_TEXT_THRESHOLD = 180

export default {
  name: 'TheHeaderQuickReplies',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 'canned_replies',
      tabs: [
        { id: 'canned_replies', label: 'Canned Replies' },
        { id: 'products', label: 'Product Details' },
      ],
      search: '',
      loading: false,
      hasLoadedOnce: false,
      error: null,
      products: [],
      cannedReplies: [],
      copiedId: null,
      copiedTimer: null,
      expandedId: null,
      previousBodyOverflow: '',
    }
  },
  computed: {
    items() {
      return this.activeTab === 'products' ? this.products : this.cannedReplies
    },
    filteredItems() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.items
      return this.items.filter((item) => {
        const title = (item.title || '').toLowerCase()
        const text = (item.text || '').toLowerCase()
        return title.includes(term) || text.includes(term)
      })
    },
    activeTabLabelLower() {
      const tab = this.tabs.find((t) => t.id === this.activeTab)
      return tab ? tab.label.toLowerCase() : ''
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.previousBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        if (!this.hasLoadedOnce && !this.loading) {
          this.fetchReplies(false)
        }
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus()
        })
      } else {
        document.body.style.overflow = this.previousBodyOverflow || ''
        this.search = ''
        this.expandedId = null
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = this.previousBodyOverflow || ''
    if (this.copiedTimer) clearTimeout(this.copiedTimer)
  },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape' && this.open) {
        this.$emit('close')
      }
    },
    countFor(tabId) {
      return tabId === 'products' ? this.products.length : this.cannedReplies.length
    },
    isLongText(text) {
      return typeof text === 'string' && text.length > LONG_TEXT_THRESHOLD
    },
    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id
    },
    async fetchReplies(forceRefresh) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(this.$apiAdress + '/api/canned_replies')
        const data = response.data || {}
        this.products = Array.isArray(data.products) ? data.products : []
        this.cannedReplies = Array.isArray(data.canned_replies) ? data.canned_replies : []
        this.hasLoadedOnce = true
        if (forceRefresh) {
          this.$toast.success('Quick replies refreshed')
        }
      } catch (err) {
        this.error = err
        if (forceRefresh) {
          this.$toast.error('Could not refresh quick replies')
        }
      } finally {
        this.loading = false
      }
    },
    async copyItem(item) {
      const text = item && typeof item.text === 'string' ? item.text : ''
      if (!text) return

      const ok = await this.writeToClipboard(text)
      if (!ok) {
        this.$toast.error('Could not copy to clipboard')
        return
      }

      this.copiedId = item.id
      if (this.copiedTimer) clearTimeout(this.copiedTimer)
      this.copiedTimer = setTimeout(() => {
        this.copiedId = null
      }, 1600)

      this.$toast.success('Copied to clipboard', {
        description: item.title,
        duration: 2200,
      })
    },
    async writeToClipboard(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
          return true
        }
      } catch (err) {
        // Fall through to legacy path.
      }

      try {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.top = '-1000px'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        const succeeded = document.execCommand('copy')
        document.body.removeChild(textarea)
        return succeeded
      } catch (err) {
        return false
      }
    },
  },
}
</script>

<style scoped>
.quick-replies__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(2px);
  z-index: 1040;
}

.quick-replies {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 100vw;
  background: #ffffff;
  border-left: 1px solid #e5e9f2;
  box-shadow: -16px 0 32px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  z-index: 1050;
}

.quick-replies__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.1rem 0.9rem;
  border-bottom: 1px solid #e5e9f2;
  background: #fbfcfe;
}

.quick-replies__header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.quick-replies__title-block {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  min-width: 0;
}

.quick-replies__accent {
  margin-top: 4px;
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  flex: 0 0 auto;
}

.quick-replies__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.quick-replies__subtitle {
  margin: 2px 0 0;
  font-size: 0.74rem;
  color: #6b7280;
  line-height: 1.3;
}

.quick-replies__header-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex: 0 0 auto;
}

.quick-replies__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.quick-replies__icon-btn:hover:not(:disabled) {
  background: #f3f6fb;
  border-color: #c7cfdb;
  color: #1f2a44;
}

.quick-replies__icon-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.quick-replies__icon-btn.is-loading svg {
  animation: quick-replies-spin 0.9s linear infinite;
}

@keyframes quick-replies-spin {
  to { transform: rotate(360deg); }
}

.quick-replies__tabs {
  display: flex;
  gap: 4px;
  padding: 0.6rem 1.1rem 0;
  background: #ffffff;
}

.quick-replies__tab {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.quick-replies__tab:hover {
  background: #f7f9fc;
  color: #1f2a44;
}

.quick-replies__tab--active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
}

.quick-replies__tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 0 6px;
  height: 18px;
  border-radius: 999px;
  background: #eef1f7;
  color: #4b5563;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.quick-replies__tab--active .quick-replies__tab-count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.quick-replies__search {
  position: relative;
  padding: 0.6rem 1.1rem 0.8rem;
  background: #ffffff;
}

.quick-replies__search-icon {
  position: absolute;
  top: 50%;
  left: 1.45rem;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.quick-replies__search-input {
  width: 100%;
  height: 38px;
  padding: 0 2.1rem 0 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #1f2a44;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.quick-replies__search-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.quick-replies__search-clear {
  position: absolute;
  top: 50%;
  right: 1.4rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: none;
  background: #eef1f7;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}

.quick-replies__search-clear:hover {
  background: #dde3ee;
  color: #1f2a44;
}

.quick-replies__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 1.1rem 1.1rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 23, 42, 0.18) transparent;
}

.quick-replies__body::-webkit-scrollbar {
  width: 6px;
}

.quick-replies__body::-webkit-scrollbar-thumb {
  background-color: rgba(15, 23, 42, 0.16);
  border-radius: 999px;
}

.quick-replies__body::-webkit-scrollbar-thumb:hover {
  background-color: rgba(15, 23, 42, 0.28);
}

.quick-replies__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.quick-replies__item {
  position: relative;
  padding: 0.7rem 0.8rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.12s ease, box-shadow 0.12s ease, transform 0.12s ease, background 0.12s ease;
}

.quick-replies__item:hover {
  border-color: #16a07a;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.quick-replies__item:active {
  transform: translateY(1px);
}

.quick-replies__item--copied {
  border-color: #16a07a;
  background: rgba(22, 160, 122, 0.06);
}

.quick-replies__item-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 4px;
}

.quick-replies__item-title {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.01em;
  line-height: 1.3;
  word-break: break-word;
}

.quick-replies__copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #4b5563;
  cursor: pointer;
  flex: 0 0 auto;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.quick-replies__item:hover .quick-replies__copy-btn {
  border-color: #16a07a;
  color: #16a07a;
}

.quick-replies__copy-btn:hover {
  background: rgba(22, 160, 122, 0.1);
}

.quick-replies__item--copied .quick-replies__copy-btn {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
}

.quick-replies__item-text {
  margin: 0;
  font-size: 0.78rem;
  color: #4b5563;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.quick-replies__item-text--expanded {
  display: block;
  -webkit-line-clamp: unset;
  line-clamp: unset;
  overflow: visible;
}

.quick-replies__item-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 6px;
  min-height: 18px;
}

.quick-replies__expand {
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.7rem;
  font-weight: 600;
  color: #16a07a;
  letter-spacing: 0.02em;
  cursor: pointer;
}

.quick-replies__expand:hover {
  text-decoration: underline;
}

.quick-replies__expand-spacer {
  display: inline-block;
  width: 1px;
}

.quick-replies__copied-flag {
  font-size: 0.66rem;
  font-weight: 700;
  color: #16a07a;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.quick-replies__skeletons {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding-top: 0.1rem;
}

.quick-replies__skeleton {
  height: 78px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f1f3f8 0%, #e8ecf3 50%, #f1f3f8 100%);
  background-size: 200% 100%;
  animation: quick-replies-shimmer 1.4s ease-in-out infinite;
}

@keyframes quick-replies-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.quick-replies__state {
  padding: 2rem 0.5rem;
  text-align: center;
  font-size: 0.82rem;
  color: #6b7280;
}

.quick-replies__state p {
  margin: 0 0 0.6rem;
}

.quick-replies__state--error {
  color: #b91c1c;
}

.quick-replies__retry {
  padding: 0.45rem 0.9rem;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  font-size: 0.78rem;
  font-weight: 600;
  color: #1f2a44;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}

.quick-replies__retry:hover {
  background: #f3f6fb;
  border-color: #c7cfdb;
}

.quick-replies-slide-enter-active,
.quick-replies-slide-leave-active {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.quick-replies-slide-enter,
.quick-replies-slide-leave-to {
  transform: translateX(100%);
}

.quick-replies-fade-enter-active,
.quick-replies-fade-leave-active {
  transition: opacity 0.18s ease;
}

.quick-replies-fade-enter,
.quick-replies-fade-leave-to {
  opacity: 0;
}

@media (max-width: 575px) {
  .quick-replies {
    width: 100%;
    border-left: 0;
  }
}
</style>
